// src/components/kuestion/campus/Assignments.jsx
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { useCampus } from './CampusContext';
import { Loading, Alert, Dialog } from '../shared/Bits';
import { SearchIcon, WarnIcon } from '../shared/Icons';
import { fmtDateTime, fullName, isOpenNow, isProctored, modeLabel, relativeWindow } from '../shared/format';
import { kq } from '../../../config/routes';

const STATUS_LABEL = { submitted: 'Submitted', in_progress: 'In progress', not_started: 'Not started' };

export default function Assignments() {
  const navigate = useNavigate();
  const { api, assignments, assignmentsLoading, reloadAssignments, students } = useCampus();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);       // expanded assignment id
  const [removing, setRemoving] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // Per-student progress comes from the single-assignment route, asked for
  // when a row is opened. Until the server sends `students` there, the row
  // falls back to the counts.
  const [detail, setDetail] = useState({});
  const toggle = (a) => {
    const next = open === a.id ? null : a.id;
    setOpen(next);
    if (next == null || detail[a.id]) return;
    api.get(API_ENDPOINTS.CAMPUS_ASSIGNMENT(a.id))
      .then((d) => setDetail((m) => ({ ...m, [a.id]: d || {} })))
      .catch(() => setDetail((m) => ({ ...m, [a.id]: {} })));
  };

  const byId = useMemo(() => new Map(students.map((s) => [String(s.id), s])), [students]);

  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    return assignments.filter((a) => !n || `${a.template_name || ''}`.toLowerCase().includes(n));
  }, [assignments, q]);

  const openCount = assignments.filter((a) => isOpenNow(a.opens_at, a.closes_at)).length;
  const done = assignments.reduce((n, a) => n + (a.completed_count || 0), 0);

  const remove = async () => {
    if (!removing) return;
    setBusy(true); setError('');
    try {
      const res = await api.del(API_ENDPOINTS.CAMPUS_ASSIGNMENT(removing.id));
      // Once anyone has sat the paper the server cancels instead of deleting,
      // and keeps their results. Say which happened.
      setNotice(res?.cancelled ? `${removing.template_name || 'The assignment'} was cancelled. ${res.sittings_kept ?? 'The'} sitting${res.sittings_kept === 1 ? '' : 's'} already taken ${res.sittings_kept === 1 ? 'was' : 'were'} kept, with results.` : '');
      setRemoving(null);
      await reloadAssignments();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="kqc-page">
      <div className="kqc-pagehead" style={{ marginBottom: 22 }}>
        <div>
          <h1 className="kqc-h1" style={{ marginBottom: 6 }}>Assignments</h1>
          <p className="kqc-small" style={{ color: 'var(--color-neutral-800)' }}>{openCount} open · {done} sitting{done === 1 ? '' : 's'} completed</p>
        </div>
        <span className="kqc-spacer" />
        <div className="kqc-search" style={{ minWidth: 200 }}>
          <span><SearchIcon /></span>
          <input type="text" placeholder="Search assignments" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <button type="button" className="btn btn-primary" onClick={() => navigate(kq('assignments/new'))} data-tour="al-new">Assign an exam</button>
      </div>

      {error && <Alert kind="error" onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert kind="info" onClose={() => setNotice('')}>{notice}</Alert>}

      {assignmentsLoading && assignments.length === 0 ? <Loading label="Loading assignments" />
        : list.length === 0 ? (
          <div className="kqc-empty">{assignments.length ? 'No assignments match.' : 'Nothing assigned yet. Pick a template, choose a mode, and give it to your students.'}</div>
        ) : (
          <div className="kqc-list">
            {list.map((a) => {
              const total = a.student_count || (a.students ? a.students.length : 0);
              const doneN = a.completed_count || (a.students ? a.students.filter((s) => s.status === 'submitted').length : 0);
              const pct = total ? Math.round((doneN / total) * 100) : 0;
              const expanded = open === a.id;
              return (
                <div key={a.id} className="kqc-list__row is-static" style={{ padding: '16px 0', display: 'block' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'clamp(12px,2vw,24px)', alignItems: 'center' }}>
                    <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                      <div className="kqc-row" style={{ gap: 9, marginBottom: 5 }}>
                        <span className="kqc-mono" style={{ fontSize: 'clamp(16px,1.9vw,20px)' }}>{a.template_name || 'Exam'}</span>
                        <span className={`tag ${isProctored(a.mode) ? 'tag-fill' : 'tag-neutral'}`}>{modeLabel(a.mode)}</span>
                        {a.is_cancelled && <span className="tag tag-outline">Cancelled</span>}
                      </div>
                      <div className="kqc-small kqc-muted">{relativeWindow(a.opens_at, a.closes_at)}{a.created_at ? ` · assigned ${fmtDateTime(a.created_at)}` : ''}</div>
                    </div>
                    <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                        <span className="kqc-mono" style={{ fontSize: 15 }}>{doneN}</span>
                        <span className="kqc-small kqc-muted">of {total} submitted</span>
                      </div>
                      <div className="kqc-bar"><i style={{ width: `${pct}%` }} /></div>
                    </div>
                    <div className="kqc-row" style={{ gap: 8, flex: '0 0 auto' }}>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => toggle(a)} aria-expanded={expanded}>{expanded ? 'Hide progress' : 'Progress'}</button>
                      <button type="button" className="btn btn-ghost btn-sm" style={{ minHeight: 0 }} onClick={() => navigate(kq('analytics'))}>Results &rarr;</button>
                      <button type="button" className="btn btn-danger btn-sm" onClick={() => setRemoving(a)}>Remove</button>
                    </div>
                  </div>

                  {expanded && (
                    <div style={{ marginTop: 14 }}>
                      {(() => { const rowsFor = detail[a.id]?.students || a.students; return Array.isArray(rowsFor) && rowsFor.length ? (
                        <div className="table-wrap">
                          <table className="table" style={{ minWidth: 520 }}>
                            <thead><tr><th>Student</th><th>Login ID</th><th>Status</th><th>Submitted</th><th>Score</th></tr></thead>
                            <tbody>
                              {rowsFor.map((s) => {
                                const st = byId.get(String(s.student_id ?? s.id));
                                const raw = s.state || s.status || (s.submitted_at ? 'submitted' : 'not_started');
                                const status = raw === 'expired' ? 'submitted' : raw; // expired is finished and scored
                                return (
                                  <tr key={s.student_id ?? s.id}>
                                    <td>{s.name || fullName(st) || '—'}</td>
                                    <td className="kqc-mono">{s.login_id || st?.login_id || '—'}</td>
                                    <td><span className={`tag ${status === 'submitted' ? 'tag-neutral' : status === 'in_progress' ? 'tag-outline' : 'tag-accent'}`}>{STATUS_LABEL[status] || status}</span></td>
                                    <td>{s.submitted_at ? fmtDateTime(s.submitted_at) : '—'}</td>
                                    <td>{(s.scored ?? s.score) != null && s.total != null ? `${s.scored ?? s.score} / ${s.total}${raw === 'expired' ? ' · ran out of time' : ''}` : s.percentage != null ? `${Math.round(s.percentage)}%` : '—'}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="kqc-small kqc-muted">{detail[a.id] ? <>The server does not list individual students for an assignment yet. {doneN} of {total} have submitted{a.started_count != null ? `, ${Math.max(0, a.started_count - doneN)} in progress` : ''}{a.expired_count ? `, ${a.expired_count} ran out of time` : ''}.</> : 'Loading progress…'}</div>
                      ); })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

      {removing && (
        <Dialog title="Remove assignment" danger icon={<WarnIcon />} onClose={busy ? null : () => setRemoving(null)}>
          <h3>Remove {removing.template_name}?</h3>
          <p className="kqc-small" style={{ color: 'var(--color-neutral-800)', marginBottom: 16 }}>
            Students will no longer see it. {isProctored(removing.mode) ? 'Completed proctored sittings stay on record for reporting.' : 'Practice results already recorded are kept.'}
          </p>
          <div className="kqc-row">
            <button type="button" className="btn btn-primary" onClick={remove} disabled={busy}>{busy ? 'Removing…' : 'Remove assignment'}</button>
            <button type="button" className="btn btn-secondary" onClick={() => setRemoving(null)} disabled={busy}>Keep it</button>
          </div>
        </Dialog>
      )}
    </div>
  );
}
